import { AxiosError, AxiosResponse, isCancel } from 'axios';
import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import { appConfig } from '../../helpers/app-config/app-config';
import type { AvailableTaskPointsResponse, GetAvailableTaskPointsPayload } from './task-points-client.type';

const axiosInstance = AxiosInstance();

/**
 * Fetches the distinct story-point estimates present within a single entity
 * scope. Used to populate the Process Analysis story-points filter so the
 * dropdown only shows point values that actually exist for the currently
 * selected portfolio / project / subproject (and so the filter can be
 * disabled when none exist).
 *
 * @param {GetAvailableTaskPointsPayload} payload - Entity scope to look up
 * @param {AbortSignal} signal - Optional AbortSignal for query cancellation
 * @returns {Promise<AvailableTaskPointsResponse>} Sorted list of story-point numbers
 * @throws {AxiosError} When the API request fails
 */
async function getAvailableTaskPoints(
  payload: GetAvailableTaskPointsPayload,
  signal?: AbortSignal,
): Promise<AvailableTaskPointsResponse> {
  try {
    const url = `${appConfig.VITE_APP_API_URL}/api/task-points/`;
    const params = new URLSearchParams();
    params.append('organization_id', payload.organizationId);
    params.append(`${payload.entityType}_id`, payload.entityId);
    if (payload.startDate) {
      params.append('start_date', payload.startDate);
    }
    if (payload.endDate) {
      params.append('end_date', payload.endDate);
    }
    const response: AxiosResponse<AvailableTaskPointsResponse> = await axiosInstance.get(url, {
      params,
      signal,
    });
    return response.data;
  } catch (error: unknown) {
    // Re-throw axios cancellation errors unchanged so TanStack Query can
    // recognize them as cancellations rather than legitimate failures.
    if (isCancel(error)) {
      return Promise.reject(error);
    }
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    }
    return Promise.reject(new Error('An unknown error occurred while fetching available task points.'));
  }
}

export { getAvailableTaskPoints };
